import React from 'react';

import { Link } from 'react-router-dom';

import { motion } from 'framer-motion';
import {
  ArrowRight,
  Award,
  BarChart2,
  Clock,
  DollarSign,
  ShieldCheck,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react';

import ModernButton from './ui/ModernButton';

interface ImpactStatsProps {
  className?: string;
}

const ImpactStats: React.FC<ImpactStatsProps> = ({ className = '' }) => {
  // Estadísticas de impacto
  const impactStats = [
    {
      icon: <TrendingUp className="h-8 w-8 text-blue-500" />,
      value: '40%',
      label: 'Aumento en ventas',
      description: 'Incremento promedio en ventas tras implementar presencia digital profesional',
    },
    {
      icon: <Users className="h-8 w-8 text-purple-500" />,
      value: '45%',
      label: 'Más clientes potenciales',
      description: 'Aumento en captación de nuevos clientes gracias a mayor visibilidad online',
    },
    {
      icon: <DollarSign className="h-8 w-8 text-green-500" />,
      value: '30%',
      label: 'Reducción costos de adquisición',
      description: 'Disminución en el costo de adquirir nuevos clientes mediante canales digitales',
    },
    {
      icon: <Award className="h-8 w-8 text-red-500" />,
      value: '65%',
      label: 'Mejora en retención de clientes',
      description: 'Mayor fidelización y recurrencia de clientes con presencia digital optimizada',
    },
  ];

  // Estadísticas de impacto a largo plazo
  const longTermStats = [
    {
      icon: <Target className="h-8 w-8 text-indigo-500" />,
      value: '3.5x',
      label: 'ROI promedio',
      description: 'Retorno de inversión promedio en todos nuestros servicios',
    },
    {
      icon: <Clock className="h-8 w-8 text-amber-500" />,
      value: '6.4',
      label: 'Meses hasta ROI positivo',
      description:
        'Tiempo promedio para alcanzar el punto de equilibrio y comenzar a generar retorno',
    },
    {
      icon: <ShieldCheck className="h-8 w-8 text-emerald-500" />,
      value: '85%',
      label: 'Reducción de riesgos',
      description: 'Disminución de vulnerabilidades operativas y tecnológicas',
    },
    {
      icon: <BarChart2 className="h-8 w-8 text-rose-500" />,
      value: '5 años',
      label: 'Ventaja competitiva',
      description: 'Duración estimada de la ventaja competitiva generada por nuestras soluciones',
    },
  ];

  return (
    <section className={`py-16 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Impacto Medible en su Negocio</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Nuestras soluciones generan resultados tangibles y cuantificables para su empresa.
            Conozca el impacto real que podemos lograr juntos, respaldado por datos y análisis de
            casos reales.
          </p>
        </div>

        {/* Estadísticas de impacto */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
          {impactStats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-md p-6 text-center"
            >
              <div className="flex justify-center mb-4">{stat.icon}</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</h3>
              <p className="text-xl font-medium text-gray-800 mb-2">{stat.label}</p>
              <p className="text-sm text-gray-600">{stat.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Estadísticas de impacto a largo plazo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
          {longTermStats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-lg shadow-md p-6 text-center"
            >
              <div className="flex justify-center mb-4">{stat.icon}</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</h3>
              <p className="text-xl font-medium text-gray-800 mb-2">{stat.label}</p>
              <p className="text-sm text-gray-600">{stat.description}</p>
            </motion.div>
          ))}
        </div>

        {/* CTA después de las estadísticas de impacto */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <div className="max-w-3xl mx-auto bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-xl shadow-md border border-blue-100">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Transforme su Negocio con Resultados Tangibles
            </h3>
            <p className="text-gray-600 mb-6">
              Descubra cómo podemos ayudarle a alcanzar estos resultados excepcionales con
              soluciones personalizadas para su empresa.
            </p>
            <Link
              to="/contacto"
              onClick={() => window.scrollTo(0, 0)}
              state={{
                subject: 'Solicitud de Consulta Exclusiva sobre Impacto',
                message:
                  'Me interesa conocer más sobre cómo puedo transformar mi negocio con resultados tangibles. Quisiera agendar una consulta exclusiva para discutir soluciones personalizadas que maximicen el impacto en mi empresa.',
              }}
            >
              <ModernButton variant="primary" size="md" className="shadow-md">
                Solicitar Consulta Exclusiva
                <ArrowRight className="ml-2 h-5 w-5" />
              </ModernButton>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ImpactStats;
